import type { HelpSectionText, LocalizedText, StaticTextKey } from "./i18n";

export interface GeneratedLanguageText {
  static: Partial<Record<StaticTextKey, string>>;
  autoLanguage: string;
  shareLabel: string;
  shareX: string;
  shareFacebook: string;
  shareClipboard: string;
  rank: string;
  canvas: LocalizedText["canvas"];
  helpSections: HelpSectionText[];
}

export const GENERATED_LANGUAGE_TEXT: Record<string, GeneratedLanguageText> = {
  "es-ES": {
    static: {
      gameActions: "Acciones del juego",
      board: "Tablero de Friel",
      status: "Estado del juego",
      topTen: "Top 10",
      touchControls: "Controles táctiles",
      left: "Izquierda",
      right: "Derecha",
      rotateLeft: "Girar a la izquierda",
      rotateRight: "Girar a la derecha",
      softDrop: "Caída suave",
      hardDrop: "Caída rápida",
      touchControlsSetting: "Controles táctiles"
    },
    autoLanguage: "Automático ({language})",
    shareLabel: "Compartir:",
    shareX: "Compartir en X",
    shareFacebook: "Compartir en Facebook",
    shareClipboard: "Copiar resultados al portapapeles",
    rank: "Puesto",
    canvas: { gameOver: "FIN DEL JUEGO", chain: "¡Cadena {value}!", colorCombos: ["", "", "¡Doble!", "¡Triple!", "¡Cuádruple!", "¡Quíntuple!", "¡Séxtuple!"], colorsFallback: "¡{value} colores!" },
    helpSections: [
      { title: "Reglas", lines: ["Cada turno cae una pareja desde arriba. Muévela, gírala alrededor del personaje que empezó abajo y colócala en la cuadrícula.", "Si dentro de cualquier zona 3x3 hay cinco o más personajes iguales, esos personajes desaparecen.", "Después de colocar o borrar, las piezas sin apoyo caen por gravedad. Si esa caída borra más piezas, se crea una cadena.", "El nivel sube cada 10 personajes borrados; los niveles 11 y 21 añaden nuevos tipos.", "La partida termina cuando la siguiente pareja no puede entrar en la fila superior visible."] },
      { title: "Teclado", lines: ["Iniciar / confirmar / continuar: Enter", "Mover: Flecha izquierda / derecha o A / D", "Girar antihorario: Q o Z", "Girar horario: E, W, X o Flecha arriba", "Caída suave: S o Flecha abajo. Caída rápida: Space", "Pausa / volver: Escape. Ayuda: F1"] },
      { title: "Botones táctiles", lines: ["Las flechas izquierda y derecha mueven una columna.", "Las flechas curvas giran en sentido antihorario u horario.", "La flecha abajo baja una fila.", "El botón de caída rápida lleva la pareja directamente a su posición de aterrizaje."] },
      { title: "Gestos del tablero", lines: ["Toca el tablero en el título para iniciar o durante pausa para continuar.", "Toca durante la partida para girar en sentido horario.", "Desliza a izquierda o derecha para mover una columna.", "Desliza hacia abajo para caída rápida y hacia arriba para girar."] },
      { title: "Mando", lines: ["La cruceta o stick izquierdo mueve la pareja.", "Abajo hace caída suave y arriba caída rápida.", "El botón inferior gira en sentido horario; el derecho gira en sentido antihorario.", "Start confirma o continúa; Select / Back pausa o cancela."] }
    ]
  },
  "fr-FR": {
    static: {
      gameActions: "Actions du jeu",
      board: "Plateau Friel",
      status: "État du jeu",
      topTen: "Top 10",
      touchControls: "Commandes tactiles",
      left: "Gauche",
      right: "Droite",
      rotateLeft: "Rotation gauche",
      rotateRight: "Rotation droite",
      softDrop: "Descente douce",
      hardDrop: "Chute directe",
      touchControlsSetting: "Commandes tactiles"
    },
    autoLanguage: "Auto ({language})",
    shareLabel: "Partager :",
    shareX: "Partager sur X",
    shareFacebook: "Partager sur Facebook",
    shareClipboard: "Copier les résultats",
    rank: "Rang",
    canvas: { gameOver: "PARTIE TERMINÉE", chain: "Chaîne {value} !", colorCombos: ["", "", "Double !", "Triple !", "Quadruple !", "Quintuple !", "Sextuple !"], colorsFallback: "{value} couleurs !" },
    helpSections: [
      { title: "Règles", lines: ["Une paire tombe depuis le haut. Déplacez-la, faites-la tourner autour du personnage placé en bas au départ, puis posez-la sur la grille.", "Cinq personnages identiques ou plus dans une zone 3x3 sont effacés.", "Après une pose ou un effacement, les personnages sans support tombent. Si cela provoque un nouvel effacement, une chaîne commence.", "Le niveau monte tous les 10 personnages effacés; les niveaux 11 et 21 ajoutent des types.", "La partie s'arrête si la prochaine paire ne peut plus entrer par la ligne supérieure visible."] },
      { title: "Clavier", lines: ["Démarrer / valider / reprendre : Enter", "Déplacer : flèches gauche / droite ou A / D", "Rotation antihoraire : Q ou Z", "Rotation horaire : E, W, X ou flèche haut", "Descente douce : S ou flèche bas. Chute directe : Space", "Pause / retour : Escape. Aide : F1"] },
      { title: "Boutons tactiles", lines: ["Les flèches gauche et droite déplacent la paire d'une colonne.", "Les flèches courbes tournent dans les deux sens.", "La flèche bas descend d'une ligne.", "La chute directe envoie la paire à sa position d'atterrissage."] },
      { title: "Gestes du plateau", lines: ["Touchez le plateau au titre pour démarrer ou en pause pour reprendre.", "Touchez pendant le jeu pour tourner dans le sens horaire.", "Balayez à gauche ou à droite pour déplacer.", "Balayez vers le bas pour chuter directement; vers le haut pour tourner."] },
      { title: "Manette", lines: ["Croix directionnelle ou stick gauche pour déplacer.", "Bas descend doucement; haut chute directement.", "Le bouton du bas tourne à droite; le bouton droit tourne à gauche.", "Start valide ou reprend; Select / Back met en pause ou annule."] }
    ]
  },
  "de-DE": {
    static: {
      gameActions: "Spielaktionen",
      board: "Friel-Spielfeld",
      status: "Spielstatus",
      topTen: "Top 10",
      touchControls: "Touch-Steuerung",
      left: "Links",
      right: "Rechts",
      rotateLeft: "Links drehen",
      rotateRight: "Rechts drehen",
      softDrop: "Weich fallen",
      hardDrop: "Sofort fallen",
      touchControlsSetting: "Touch-Steuerung"
    },
    autoLanguage: "Automatisch ({language})",
    shareLabel: "Teilen:",
    shareX: "Auf X teilen",
    shareFacebook: "Auf Facebook teilen",
    shareClipboard: "Ergebnisse kopieren",
    rank: "Rang",
    canvas: { gameOver: "SPIEL VORBEI", chain: "{value}er Kette!", colorCombos: ["", "", "Doppelt!", "Dreifach!", "Vierfach!", "Fünffach!", "Sechsfach!"], colorsFallback: "{value} Farben!" },
    helpSections: [
      { title: "Regeln", lines: ["Ein Paar fällt von oben. Bewege es, drehe es um die Figur, die zuerst unten war, und setze es ins Raster.", "Fünf oder mehr gleiche Figuren in einem beliebigen 3x3-Bereich werden gelöscht.", "Nach dem Landen oder Löschen fallen nicht gestützte Figuren nach unten. Entsteht dadurch ein weiterer Löschvorgang, zählt er als Kette.", "Alle 10 gelöschten Figuren steigt das Level; Level 11 und 21 fügen neue Typen hinzu.", "Das Spiel endet, wenn das nächste Paar nicht mehr in die sichtbare obere Reihe eintreten kann."] },
      { title: "Tastatur", lines: ["Start / bestätigen / fortsetzen: Enter", "Bewegen: Pfeil links / rechts oder A / D", "Gegen Uhrzeigersinn drehen: Q oder Z", "Im Uhrzeigersinn drehen: E, W, X oder Pfeil hoch", "Weich fallen: S oder Pfeil runter. Sofort fallen: Space", "Pause / zurück: Escape. Hilfe: F1"] },
      { title: "Touch-Tasten", lines: ["Links- und Rechtspfeil bewegen eine Spalte.", "Gebogene Pfeile drehen in beide Richtungen.", "Der Pfeil nach unten senkt um eine Reihe.", "Sofort-Fallen bringt das Paar direkt zur Landeposition."] },
      { title: "Brettgesten", lines: ["Tippe im Titel auf das Feld zum Starten oder in Pause zum Fortsetzen.", "Tippen im Spiel dreht im Uhrzeigersinn.", "Wische links oder rechts zum Bewegen.", "Wische nach unten zum Sofort-Fallen und nach oben zum Drehen."] },
      { title: "Gamepad", lines: ["Steuerkreuz oder linker Stick bewegt das Paar.", "Nach unten fällt weich, nach oben sofort.", "Untere Taste dreht rechts, rechte Taste dreht links.", "Start bestätigt oder setzt fort; Select / Back pausiert oder bricht ab."] }
    ]
  },
  "pt-BR": {
    static: {
      gameActions: "Ações do jogo",
      board: "Tabuleiro Friel",
      status: "Estado do jogo",
      topTen: "Top 10",
      touchControls: "Controles de toque",
      left: "Esquerda",
      right: "Direita",
      rotateLeft: "Girar à esquerda",
      rotateRight: "Girar à direita",
      softDrop: "Queda suave",
      hardDrop: "Queda rápida",
      touchControlsSetting: "Controles de toque"
    },
    autoLanguage: "Automático ({language})",
    shareLabel: "Compartilhar:",
    shareX: "Compartilhar no X",
    shareFacebook: "Compartilhar no Facebook",
    shareClipboard: "Copiar resultados",
    rank: "Posição",
    canvas: { gameOver: "FIM DE JOGO", chain: "Cadeia {value}!", colorCombos: ["", "", "Duplo!", "Triplo!", "Quádruplo!", "Quíntuplo!", "Sêxtuplo!"], colorsFallback: "{value} cores!" },
    helpSections: [
      { title: "Regras", lines: ["Um par cai do topo. Mova, gire em torno do personagem que começou embaixo e encaixe na grade.", "Cinco ou mais personagens iguais dentro de uma área 3x3 são removidos.", "Depois de pousar ou remover, personagens sem apoio caem. Se a queda criar outra remoção, isso vira cadeia.", "O nível sobe a cada 10 removidos; os níveis 11 e 21 adicionam novos tipos.", "O jogo termina quando o próximo par não consegue entrar na linha superior visível."] },
      { title: "Teclado", lines: ["Iniciar / confirmar / continuar: Enter", "Mover: Setas esquerda / direita ou A / D", "Girar anti-horário: Q ou Z", "Girar horário: E, W, X ou seta para cima", "Queda suave: S ou seta para baixo. Queda rápida: Space", "Pausa / voltar: Escape. Ajuda: F1"] },
      { title: "Botões de toque", lines: ["Setas esquerda e direita movem uma coluna.", "Setas curvas giram nos dois sentidos.", "Seta para baixo desce uma linha.", "Queda rápida leva o par direto ao ponto de pouso."] },
      { title: "Gestos no tabuleiro", lines: ["Toque no tabuleiro no título para começar ou em pausa para continuar.", "Toque durante o jogo para girar no sentido horário.", "Deslize para esquerda ou direita para mover.", "Deslize para baixo para queda rápida e para cima para girar."] },
      { title: "Controle", lines: ["Direcional ou analógico esquerdo move o par.", "Baixo faz queda suave; cima faz queda rápida.", "Botão inferior gira à direita; botão direito gira à esquerda.", "Start confirma ou continua; Select / Back pausa ou cancela."] }
    ]
  },
  "it-IT": {
    static: {
      gameActions: "Azioni di gioco",
      board: "Griglia Friel",
      status: "Stato della partita",
      topTen: "Top 10",
      touchControls: "Comandi touch",
      left: "Sinistra",
      right: "Destra",
      rotateLeft: "Ruota a sinistra",
      rotateRight: "Ruota a destra",
      softDrop: "Discesa morbida",
      hardDrop: "Caduta rapida",
      touchControlsSetting: "Comandi touch"
    },
    autoLanguage: "Automatico ({language})",
    shareLabel: "Condividi:",
    shareX: "Condividi su X",
    shareFacebook: "Condividi su Facebook",
    shareClipboard: "Copia risultati",
    rank: "Posizione",
    canvas: { gameOver: "PARTITA FINITA", chain: "Catena {value}!", colorCombos: ["", "", "Doppio!", "Triplo!", "Quadruplo!", "Quintuplo!", "Sestuplo!"], colorsFallback: "{value} colori!" },
    helpSections: [
      { title: "Regole", lines: ["Una coppia cade dall'alto. Spostala, ruotala intorno al personaggio inizialmente in basso e falla atterrare nella griglia.", "Cinque o più personaggi uguali in una zona 3x3 vengono eliminati.", "Dopo atterraggio o eliminazione, i personaggi senza supporto cadono. Se creano un'altra eliminazione, nasce una catena.", "Il livello sale ogni 10 eliminazioni; i livelli 11 e 21 aggiungono nuovi tipi.", "La partita finisce quando la coppia successiva non può entrare nella riga superiore visibile."] },
      { title: "Tastiera", lines: ["Avvia / conferma / riprendi: Enter", "Sposta: Freccia sinistra / destra o A / D", "Ruota antiorario: Q o Z", "Ruota orario: E, W, X o Freccia su", "Discesa morbida: S o Freccia giù. Caduta rapida: Space", "Pausa / indietro: Escape. Aiuto: F1"] },
      { title: "Pulsanti touch", lines: ["Le frecce sinistra e destra spostano di una colonna.", "Le frecce curve ruotano nei due sensi.", "La freccia giù scende di una riga.", "Caduta rapida porta la coppia direttamente al punto di atterraggio."] },
      { title: "Gesti sulla griglia", lines: ["Tocca la griglia nel titolo per avviare o in pausa per riprendere.", "Tocca durante la partita per ruotare in senso orario.", "Scorri a sinistra o destra per spostare.", "Scorri in basso per caduta rapida e in alto per ruotare."] },
      { title: "Controller", lines: ["D-pad o stick sinistro spostano la coppia.", "Giù fa discesa morbida, su caduta rapida.", "Il pulsante inferiore ruota a destra; quello destro ruota a sinistra.", "Start conferma o riprende; Select / Back mette in pausa o annulla."] }
    ]
  },
  "ru-RU": {
    static: {
      gameActions: "Действия игры",
      board: "Поле Friel",
      status: "Состояние игры",
      topTen: "Топ-10",
      touchControls: "Сенсорное управление",
      left: "Влево",
      right: "Вправо",
      rotateLeft: "Поворот влево",
      rotateRight: "Поворот вправо",
      softDrop: "Мягкое падение",
      hardDrop: "Быстрое падение",
      touchControlsSetting: "Сенсорное управление"
    },
    autoLanguage: "Авто ({language})",
    shareLabel: "Поделиться:",
    shareX: "Поделиться в X",
    shareFacebook: "Поделиться в Facebook",
    shareClipboard: "Скопировать результаты",
    rank: "Место",
    canvas: { gameOver: "ИГРА ОКОНЧЕНА", chain: "Цепь {value}!", colorCombos: ["", "", "Дубль!", "Тройка!", "Четыре!", "Пять!", "Шесть!"], colorsFallback: "{value} цветов!" },
    helpSections: [
      { title: "Правила", lines: ["Пара падает сверху. Перемещайте ее, вращайте вокруг персонажа, который сначала был снизу, и ставьте на поле.", "Пять или больше одинаковых персонажей в любой зоне 3x3 исчезают.", "После приземления или удаления неподдержанные персонажи падают. Если это создает новое удаление, начинается цепь.", "Уровень растет за каждые 10 удалений; уровни 11 и 21 добавляют новые типы.", "Игра заканчивается, если следующая пара не может войти в видимую верхнюю строку."] },
      { title: "Клавиатура", lines: ["Старт / подтверждение / продолжить: Enter", "Движение: стрелки влево / вправо или A / D", "Поворот против часовой: Q или Z", "Поворот по часовой: E, W, X или стрелка вверх", "Мягкое падение: S или стрелка вниз. Быстрое падение: Space", "Пауза / назад: Escape. Справка: F1"] },
      { title: "Сенсорные кнопки", lines: ["Стрелки влево и вправо двигают на один столбец.", "Изогнутые стрелки вращают в обе стороны.", "Стрелка вниз опускает на одну строку.", "Быстрое падение сразу переносит пару к месту посадки."] },
      { title: "Жесты на поле", lines: ["Нажмите поле на титуле для старта или в паузе для продолжения.", "Нажатие во время игры вращает по часовой стрелке.", "Смахивание влево или вправо двигает на столбец.", "Смахивание вниз быстро роняет, вверх вращает."] },
      { title: "Геймпад", lines: ["Крестовина или левый стик двигают пару.", "Вниз — мягкое падение, вверх — быстрое.", "Нижняя кнопка вращает по часовой, правая — против.", "Start подтверждает или продолжает; Select / Back ставит паузу или отменяет."] }
    ]
  },
  ar: {
    static: {
      gameActions: "إجراءات اللعبة",
      board: "لوحة Friel",
      status: "حالة اللعبة",
      topTen: "أفضل 10",
      touchControls: "تحكم باللمس",
      left: "يسار",
      right: "يمين",
      rotateLeft: "تدوير عكس الساعة",
      rotateRight: "تدوير مع الساعة",
      softDrop: "إنزال بطيء",
      hardDrop: "إسقاط سريع",
      touchControlsSetting: "تحكم باللمس"
    },
    autoLanguage: "تلقائي ({language})",
    shareLabel: "مشاركة:",
    shareX: "مشاركة على X",
    shareFacebook: "مشاركة على Facebook",
    shareClipboard: "نسخ النتائج",
    rank: "الترتيب",
    canvas: { gameOver: "انتهت اللعبة", chain: "سلسلة {value}!", colorCombos: ["", "", "مزدوج!", "ثلاثي!", "رباعي!", "خماسي!", "سداسي!"], colorsFallback: "{value} ألوان!" },
    helpSections: [
      { title: "القواعد", lines: ["يسقط زوج من الأعلى. حرّكه ودوّره حول الشخصية التي كانت في الأسفل أولاً ثم ضعه في الشبكة.", "إذا وُجدت خمسة شخصيات متشابهة أو أكثر داخل أي منطقة 3x3 فستُمحى.", "بعد الهبوط أو المحو، تسقط الشخصيات غير المدعومة بالجاذبية. إذا سببت محواً آخر تصبح سلسلة.", "يرتفع المستوى كل 10 شخصيات ممحوّة؛ المستويان 11 و21 يضيفان أنواعاً جديدة.", "تنتهي اللعبة عندما لا يستطيع الزوج التالي دخول الصف العلوي المرئي."] },
      { title: "لوحة المفاتيح", lines: ["بدء / تأكيد / استئناف: Enter", "تحريك: السهم الأيسر / الأيمن أو A / D", "تدوير عكس الساعة: Q أو Z", "تدوير مع الساعة: E أو W أو X أو السهم للأعلى", "إنزال بطيء: S أو السهم للأسفل. إسقاط سريع: Space", "إيقاف مؤقت / رجوع: Escape. مساعدة: F1"] },
      { title: "أزرار اللمس", lines: ["السهمان الأيسر والأيمن يحركان عموداً واحداً.", "الأسهم المنحنية تدور في الاتجاهين.", "السهم للأسفل ينزل صفاً واحداً.", "زر الإسقاط السريع ينقل الزوج مباشرة إلى موضع الهبوط."] },
      { title: "إيماءات اللوحة", lines: ["المس اللوحة في شاشة العنوان للبدء أو أثناء الإيقاف للاستئناف.", "المس أثناء اللعب للتدوير مع الساعة.", "اسحب يساراً أو يميناً للتحريك.", "اسحب للأسفل للإسقاط السريع وللأعلى للتدوير."] },
      { title: "يد التحكم", lines: ["لوحة الاتجاهات أو العصا اليسرى تحرك الزوج.", "الأسفل إنزال بطيء، والأعلى إسقاط سريع.", "الزر السفلي يدور مع الساعة، والزر الأيمن عكس الساعة.", "Start للتأكيد أو الاستئناف؛ Select / Back للإيقاف أو الإلغاء."] }
    ]
  },
  "hi-IN": {
    static: {
      gameActions: "गेम क्रियाएँ",
      board: "Friel बोर्ड",
      status: "गेम स्थिति",
      topTen: "शीर्ष 10",
      touchControls: "टच नियंत्रण",
      left: "बाएँ",
      right: "दाएँ",
      rotateLeft: "बाएँ घुमाएँ",
      rotateRight: "दाएँ घुमाएँ",
      softDrop: "धीमा ड्रॉप",
      hardDrop: "हार्ड ड्रॉप",
      touchControlsSetting: "टच नियंत्रण"
    },
    autoLanguage: "स्वचालित ({language})",
    shareLabel: "साझा करें:",
    shareX: "X पर साझा करें",
    shareFacebook: "Facebook पर साझा करें",
    shareClipboard: "परिणाम कॉपी करें",
    rank: "रैंक",
    canvas: { gameOver: "गेम समाप्त", chain: "{value} चेन!", colorCombos: ["", "", "डबल!", "ट्रिपल!", "क्वाड्रुपल!", "क्विंटुपल!", "सेक्स्टुपल!"], colorsFallback: "{value} रंग!" },
    helpSections: [
      { title: "नियम", lines: ["एक जोड़ी ऊपर से गिरती है। उसे चलाएँ, शुरुआत में नीचे रहे पात्र को धुरी बनाकर घुमाएँ और ग्रिड पर उतारें।", "किसी भी 3x3 क्षेत्र में पाँच या अधिक समान पात्र हों तो वे मिट जाते हैं।", "उतरने या मिटने के बाद बिना सहारे वाले पात्र गुरुत्व से गिरते हैं। इससे फिर मिटे तो चेन बनती है।", "हर 10 मिटे पात्रों पर स्तर बढ़ता है; स्तर 11 और 21 पर नए प्रकार जुड़ते हैं।", "जब अगली जोड़ी दिखाई देने वाली सबसे ऊपर की पंक्ति में प्रवेश नहीं कर सकती, गेम समाप्त होता है।"] },
      { title: "कीबोर्ड", lines: ["शुरू / पुष्टि / फिर शुरू: Enter", "चलाएँ: बायाँ / दायाँ तीर या A / D", "वामावर्त घुमाएँ: Q या Z", "दक्षिणावर्त घुमाएँ: E, W, X या ऊपर तीर", "धीमा ड्रॉप: S या नीचे तीर। हार्ड ड्रॉप: Space", "रोकें / वापस: Escape। सहायता: F1"] },
      { title: "टच बटन", lines: ["बाएँ और दाएँ तीर एक कॉलम चलाते हैं।", "मुड़े हुए तीर दोनों दिशाओं में घुमाते हैं।", "नीचे तीर एक पंक्ति गिराता है।", "हार्ड ड्रॉप बटन जोड़ी को सीधे उतरने की जगह तक भेजता है।"] },
      { title: "बोर्ड जेस्चर", lines: ["शीर्ष स्क्रीन पर बोर्ड टैप करें तो शुरू, pause में टैप करें तो फिर शुरू।", "खेलते समय टैप करने से दक्षिणावर्त घुमता है।", "बाएँ या दाएँ स्वाइप से एक कॉलम चलता है।", "नीचे स्वाइप हार्ड ड्रॉप और ऊपर स्वाइप घुमाव करता है।"] },
      { title: "गेमपैड", lines: ["D-pad या बायाँ स्टिक जोड़ी को चलाता है।", "नीचे धीमा ड्रॉप, ऊपर हार्ड ड्रॉप करता है।", "नीचे वाला बटन दक्षिणावर्त और दायाँ बटन वामावर्त घुमाता है।", "Start पुष्टि या फिर शुरू करता है; Select / Back pause या cancel करता है।"] }
    ]
  },
  "id-ID": {
    static: {
      gameActions: "Aksi game",
      board: "Papan Friel",
      status: "Status game",
      topTen: "10 Teratas",
      touchControls: "Kontrol sentuh",
      left: "Kiri",
      right: "Kanan",
      rotateLeft: "Putar kiri",
      rotateRight: "Putar kanan",
      softDrop: "Turun pelan",
      hardDrop: "Jatuh cepat",
      touchControlsSetting: "Kontrol sentuh"
    },
    autoLanguage: "Otomatis ({language})",
    shareLabel: "Bagikan:",
    shareX: "Bagikan ke X",
    shareFacebook: "Bagikan ke Facebook",
    shareClipboard: "Salin hasil",
    rank: "Peringkat",
    canvas: { gameOver: "GAME SELESAI", chain: "Rantai {value}!", colorCombos: ["", "", "Ganda!", "Triple!", "Empat!", "Lima!", "Enam!"], colorsFallback: "{value} warna!" },
    helpSections: [
      { title: "Aturan", lines: ["Sepasang karakter jatuh dari atas. Geser, putar mengelilingi karakter yang awalnya di bawah, lalu daratkan di grid.", "Jika ada lima atau lebih karakter sama dalam area 3x3, karakter itu terhapus.", "Setelah mendarat atau terhapus, karakter tanpa penyangga jatuh oleh gravitasi. Jika memicu hapus lagi, itu menjadi rantai.", "Level naik tiap 10 karakter terhapus; level 11 dan 21 menambah jenis baru.", "Game berakhir saat pasangan berikutnya tidak bisa masuk ke baris atas yang terlihat."] },
      { title: "Keyboard", lines: ["Mulai / konfirmasi / lanjut: Enter", "Gerak: panah kiri / kanan atau A / D", "Putar berlawanan jarum jam: Q atau Z", "Putar searah jarum jam: E, W, X atau panah atas", "Turun pelan: S atau panah bawah. Jatuh cepat: Space", "Pause / kembali: Escape. Bantuan: F1"] },
      { title: "Tombol sentuh", lines: ["Panah kiri dan kanan menggeser satu kolom.", "Panah melengkung memutar ke dua arah.", "Panah bawah turun satu baris.", "Tombol jatuh cepat membawa pasangan langsung ke posisi mendarat."] },
      { title: "Gestur papan", lines: ["Ketuk papan di judul untuk mulai atau saat pause untuk lanjut.", "Ketuk saat bermain untuk putar kanan.", "Geser kiri atau kanan untuk berpindah kolom.", "Geser turun untuk jatuh cepat dan geser naik untuk memutar."] },
      { title: "Gamepad", lines: ["D-pad atau stick kiri menggerakkan pasangan.", "Bawah turun pelan, atas jatuh cepat.", "Tombol bawah memutar kanan; tombol kanan memutar kiri.", "Start mengonfirmasi atau lanjut; Select / Back pause atau batal."] }
    ]
  }
};
